import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import PhotoLightbox from '@/components/PhotoLightbox';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Camera, Download, ExternalLink, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { canEdit, isPhotoEditor } from '@/lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
};

const photoUrl = (p) => (p?.url?.startsWith('http') ? p.url : `${BACKEND_URL}${p?.url || ''}`);

// Org-wide photo gallery: every high-res upload across every event the user
// can see. Useful for editors who triage downloads across multiple jobs.
export default function Photos() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');
  const [photographerFilter, setPhotographerFilter] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [groupBy, setGroupBy] = useState('event'); // event | photographer | spot
  const [zipping, setZipping] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, list: [], index: 0 });

  const canDelete = canEdit(user, 'spots') || isPhotoEditor(user);
  const canEditNote = canDelete; // same gate — someone who can delete can annotate

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (eventFilter !== 'all') params.event_id = eventFilter;
      if (photographerFilter.trim()) params.photographer = photographerFilter.trim();
      const r = await axios.get(`${API}/photos`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setEvents(r.data.events || []);
      setPhotos(r.data.photos || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('photos.loadError', { defaultValue: 'Could not load photos' }));
    } finally {
      setLoading(false);
    }
  }, [eventFilter, photographerFilter, t]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (photo) => {
    if (!window.confirm(t('photos.removeConfirm', { defaultValue: 'Remove this photo?' }))) return;
    try {
      await axios.delete(`${API}/spot-photos/${photo.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setLightbox((lb) => {
        if (!lb.open) return lb;
        const newList = lb.list.filter((p) => p.id !== photo.id);
        if (newList.length === 0) return { open: false, list: [], index: 0 };
        return { ...lb, list: newList, index: Math.min(lb.index, newList.length - 1) };
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('photos.removeError', { defaultValue: 'Could not remove' }));
    }
  };

  const saveNote = async (photo, note) => {
    try {
      const r = await axios.patch(
        `${API}/spot-photos/${photo.id}`,
        { note },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const updated = r.data;
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, note: updated.note } : p)));
      setLightbox((lb) => ({
        ...lb,
        list: lb.list.map((p) => (p.id === photo.id ? { ...p, note: updated.note } : p)),
      }));
      toast.success(t('photos.noteSaved', { defaultValue: 'Note saved' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('photos.noteError', { defaultValue: 'Could not save note' }));
    }
  };

  const downloadAll = async () => {
    setZipping(true);
    try {
      const params = new URLSearchParams();
      if (eventFilter !== 'all') params.set('event_id', eventFilter);
      if (photographerFilter.trim()) params.set('photographer', photographerFilter.trim());
      const r = await axios.get(`${API}/photos/download?${params.toString()}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const blob = new Blob([r.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photoplan-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('photos.zipError', { defaultValue: 'Could not build ZIP' }));
    } finally {
      setZipping(false);
    }
  };

  const filtered = useMemo(() => {
    const lower = textFilter.trim().toLowerCase();
    if (!lower) return photos;
    return photos.filter((p) => {
      const hay = [p.event_name, p.event_client, p.spot_name, p.photographer_name, p.filename, p.note]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(lower);
    });
  }, [photos, textFilter]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    for (const p of filtered) {
      let key;
      let header;
      if (groupBy === 'photographer') {
        key = p.photographer_name || '—';
        header = { title: p.photographer_name || t('photos.unknownPhotographer', { defaultValue: 'Unknown photographer' }) };
      } else if (groupBy === 'spot') {
        key = `${p.event_id}::${p.spot_id}`;
        header = {
          title: p.spot_name || t('photos.unknownSpot', { defaultValue: 'Unknown spot' }),
          subtitle: p.event_name,
          eventId: p.event_id,
        };
      } else {
        key = p.event_id || 'unknown';
        header = {
          title: p.event_name || t('photos.unknownEvent', { defaultValue: 'Unknown event' }),
          subtitle: p.event_client,
          eventId: p.event_id,
        };
      }
      if (!buckets.has(key)) buckets.set(key, { key, header, photos: [] });
      buckets.get(key).photos.push(p);
    }
    return Array.from(buckets.values());
  }, [filtered, groupBy, t]);

  // Latest 12 — always sorted by uploaded_at desc by the API.
  const latest = useMemo(() => filtered.slice(0, 12), [filtered]);

  const totalCount = filtered.length;
  const totalBytes = filtered.reduce((sum, p) => sum + (p.size_bytes || 0), 0);

  const openLightbox = (list, photo) => {
    const idx = list.findIndex((p) => p.id === photo.id);
    if (idx >= 0) setLightbox({ open: true, list, index: idx });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="md:ml-64 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Camera className="w-7 h-7 text-emerald-600" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                {t('photos.title', { defaultValue: 'Photos' })}
              </h1>
              <p className="text-sm text-slate-500">
                {t('photos.subtitle', { defaultValue: 'High-res uploads across every event you can access.' })}
              </p>
            </div>
          </div>

          <Card className="glass-card p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t('photos.filterEvent', { defaultValue: 'Event' })}
                </label>
                <Select value={eventFilter} onValueChange={setEventFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t('photos.allEvents', { defaultValue: 'All events' })}
                    </SelectItem>
                    {events.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t('photos.filterPhotographer', { defaultValue: 'Photographer' })}
                </label>
                <Input
                  value={photographerFilter}
                  onChange={(e) => setPhotographerFilter(e.target.value)}
                  placeholder={t('photos.photographerPlaceholder', { defaultValue: 'name contains…' })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t('photos.filterText', { defaultValue: 'Search' })}
                </label>
                <Input
                  value={textFilter}
                  onChange={(e) => setTextFilter(e.target.value)}
                  placeholder={t('photos.textPlaceholder', { defaultValue: 'event, spot, file, note…' })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t('photos.groupBy', { defaultValue: 'Group by' })}
                </label>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event">{t('photos.groupEvent', { defaultValue: 'Event' })}</SelectItem>
                    <SelectItem value="spot">{t('photos.groupSpot', { defaultValue: 'Spot' })}</SelectItem>
                    <SelectItem value="photographer">{t('photos.groupPhotographer', { defaultValue: 'Photographer' })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t flex-wrap gap-2">
              <p className="text-sm text-slate-600">
                {totalCount} {t('photos.photo', { defaultValue: 'photos' })} · {fmtBytes(totalBytes) || '0B'} {t('photos.total', { defaultValue: 'total' })}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
                  {loading ? t('photos.loading', { defaultValue: 'Loading…' }) : t('photos.refresh', { defaultValue: 'Refresh' })}
                </Button>
                <Button size="sm" onClick={downloadAll} disabled={zipping || totalCount === 0}>
                  <Download className="w-4 h-4 mr-1" />
                  {zipping
                    ? t('photos.zipping', { defaultValue: 'Building ZIP…' })
                    : t('photos.downloadAll', { defaultValue: 'Download all (ZIP)' })}
                </Button>
              </div>
            </div>
          </Card>

          {totalCount === 0 ? (
            <Card className="glass-card p-12 text-center text-slate-500">
              <Camera className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p className="font-medium">
                {t('photos.empty', { defaultValue: 'No photos uploaded yet.' })}
              </p>
              <p className="text-xs mt-2 max-w-md mx-auto">
                {t('photos.emptyHint', {
                  defaultValue: 'Photographers attach originals from the share link after they check out of a spot. Anything they upload will show up here automatically.',
                })}
              </p>
            </Card>
          ) : (
            <>
              {/* Latest strip — always pinned at the top so the most recent
                  uploads are visible without scrolling. */}
              {latest.length > 0 && (
                <Card className="glass-card p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-semibold text-slate-900">
                      🆕 {t('photos.latest', { defaultValue: 'Latest uploads' })}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {t('photos.latestHint', { defaultValue: 'Most recent first' })}
                    </p>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {latest.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => openLightbox(filtered, p)}
                        className="relative flex-shrink-0 w-32 h-32 rounded overflow-hidden border bg-slate-100 hover:ring-2 hover:ring-emerald-500"
                        title={p.filename || ''}
                      >
                        <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5">
                          <p className="truncate">{p.photographer_name || '—'}</p>
                          <p className="truncate text-slate-300">{p.spot_name || ''}</p>
                        </div>
                        {p.note && (
                          <div className="absolute top-1 right-1 bg-amber-400/90 rounded p-0.5">
                            <FileText className="w-3 h-3 text-amber-900" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </Card>
              )}

              <div className="space-y-6">
                {grouped.map((g) => (
                  <Card key={g.key} className="glass-card p-4">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          {g.header.title}
                          {g.header.eventId && (
                            <Link
                              to={`/events/${g.header.eventId}?tab=photos`}
                              className="text-xs text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {t('photos.openEvent', { defaultValue: 'Open event' })}
                            </Link>
                          )}
                        </h3>
                        {g.header.subtitle && (
                          <p className="text-xs text-slate-500">{g.header.subtitle}</p>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">
                        {g.photos.length} · {fmtBytes(g.photos.reduce((s, p) => s + (p.size_bytes || 0), 0))}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {g.photos.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => openLightbox(g.photos, p)}
                          className="relative group border rounded overflow-hidden bg-slate-50 text-left hover:ring-2 hover:ring-emerald-500"
                        >
                          <img src={photoUrl(p)} alt="" className="w-full h-32 object-cover" />
                          <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                            {fmtBytes(p.size_bytes)}
                          </div>
                          {p.note && (
                            <div className="absolute top-1 right-1 bg-amber-400/90 rounded p-0.5" title={p.note}>
                              <FileText className="w-3 h-3 text-amber-900" />
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5">
                            <p className="truncate">{p.photographer_name || '—'}</p>
                            {groupBy !== 'spot' && p.spot_name && (
                              <p className="truncate text-slate-300">{p.spot_name}</p>
                            )}
                            {groupBy !== 'event' && p.event_name && (
                              <p className="truncate text-slate-300">{p.event_name}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {lightbox.open && (
        <PhotoLightbox
          photos={lightbox.list}
          index={lightbox.index}
          onClose={() => setLightbox({ open: false, list: [], index: 0 })}
          onPrev={() => setLightbox((lb) => ({ ...lb, index: Math.max(0, lb.index - 1) }))}
          onNext={() => setLightbox((lb) => ({ ...lb, index: Math.min(lb.list.length - 1, lb.index + 1) }))}
          canEditNote={canEditNote}
          onSaveNote={saveNote}
          canDelete={canDelete}
          onDelete={remove}
        />
      )}
    </div>
  );
}
